// "Talk to HR": an employee hands a question to a person. The request carries who they are (the
// only place Ask HR attaches identity to what someone asked), a category, an optional note, and,
// if they choose, the conversation it came from. HR is emailed and works it in the Requests queue.

import { BadRequestError } from "./sources.js";

export const CATEGORIES = {
  leave: "Leave or time off",
  pay: "Pay or benefits",
  policy: "A policy question",
  concern: "A workplace concern",
  other: "Something else",
} as const;

export type Category = keyof typeof CATEGORIES;
export type EscalationStatus = "new" | "in_progress" | "resolved";
export type Channel = "web" | "slack";

export const STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

export interface ExcerptTurn {
  role: "user" | "assistant";
  text: string;
}

export interface EscalationEvent {
  at: string;
  by: string;
  status?: EscalationStatus;
  note?: string;
}

export interface Escalation {
  id: string;
  status: EscalationStatus;
  category: Category;
  note: string;
  channel: Channel;
  requester: { oid: string; name: string; email: string };
  location: string; // "California", "Another US state", "Canada", or "Not set"
  conversationId?: string;
  excerpt: ExcerptTurn[];
  createdAt: string;
  updatedAt: string;
  history: EscalationEvent[];
  emailed: boolean;
}

export interface EscalationInput {
  category: Category;
  note: string;
  conversationId?: string;
  includeConversation: boolean;
}

export const MAX_NOTE_CHARS = 2000;
export const MAX_EXCERPT_TURNS = 12;

export function isCategory(v: unknown): v is Category {
  return typeof v === "string" && Object.hasOwn(CATEGORIES, v);
}

export function isStatus(v: unknown): v is EscalationStatus {
  return v === "new" || v === "in_progress" || v === "resolved";
}

export function parseEscalationInput(body: unknown): EscalationInput {
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  if (!isCategory(b.category)) throw new BadRequestError("Choose what this is about.");
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length > MAX_NOTE_CHARS) throw new BadRequestError(`Notes can be up to ${MAX_NOTE_CHARS} characters.`);
  const conversationId = typeof b.conversationId === "string" && b.conversationId ? b.conversationId : undefined;
  const includeConversation = b.includeConversation !== false && Boolean(conversationId);
  if (!note && !includeConversation) throw new BadRequestError("Add a note, or include the conversation, so HR knows what you need.");
  return { category: b.category, note, ...(conversationId ? { conversationId } : {}), includeConversation };
}

// ---- the email to HR (plain text and a simple HTML version; every value escaped)

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function escalationEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  const subject = `Ask HR: ${CATEGORIES[e.category]} from ${e.requester.name}`;
  const lines = [
    `${e.requester.name} (${e.requester.email}) asked to talk to HR${e.channel === "slack" ? " from Slack" : ""}.`,
    "",
    `About: ${CATEGORIES[e.category]}`,
    `Works in: ${e.location}`,
    ...(e.note ? ["", "Their note:", e.note] : []),
    ...(e.excerpt.length
      ? ["", "The conversation with Ask HR:", ...e.excerpt.map((t) => `${t.role === "user" ? e.requester.name : "Ask HR"}: ${t.text}`)]
      : []),
    "",
    `Reply to this email to answer them directly. Track it in the HR queue: ${link}`,
  ];
  const html = [
    `<p><strong>${esc(e.requester.name)}</strong> (<a href="mailto:${esc(e.requester.email)}">${esc(e.requester.email)}</a>) asked to talk to HR${e.channel === "slack" ? " from Slack" : ""}.</p>`,
    `<p>About: <strong>${esc(CATEGORIES[e.category])}</strong><br>Works in: ${esc(e.location)}</p>`,
    e.note ? `<p>Their note:</p><blockquote style="white-space:pre-wrap">${esc(e.note)}</blockquote>` : "",
    e.excerpt.length
      ? `<p>The conversation with Ask HR:</p>${e.excerpt
          .map((t) => `<p style="white-space:pre-wrap"><strong>${t.role === "user" ? esc(e.requester.name) : "Ask HR"}:</strong> ${esc(t.text)}</p>`)
          .join("")}`
      : "",
    `<p>Reply to this email to answer them directly. <a href="${esc(link)}">Track it in the HR queue</a>.</p>`,
  ].join("\n");
  return { subject, text: lines.join("\n"), html };
}
